import React, { useState } from 'react';
import { Info, Grid3x3, List, Star, Menu } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { mockExperiences } from '../data/mockExperiences';
import { ExperienceBubbleCluster } from './ExperienceBubbleCluster';
import { ExperiencesList } from './ExperiencesList';
import { BubbleLegend } from './BubbleLegend';
import { ExperienceDetailView } from './ExperienceDetailView';
import { FavoritesModal } from './FavoritesModal';
type ViewMode = 'bubbles' | 'list';
interface ExperiencesBubbleScreenProps {
  onMenuClick: () => void;
}
export function ExperiencesBubbleScreen({
  onMenuClick
}: ExperiencesBubbleScreenProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('bubbles');
  const [selectedExperienceId, setSelectedExperienceId] = useState<string | null>(null);
  const [expandedExperienceId, setExpandedExperienceId] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  // Mock favorites - in real app, this would come from state/storage
  const favoriteExperiences = mockExperiences.filter(exp => ['b1', 'a3', 'c1', 'f1'].includes(exp.id));
  const selectedExperience = mockExperiences.find(exp => exp.id === selectedExperienceId);
  const expandedExperience = mockExperiences.find(exp => exp.id === expandedExperienceId);
  const handleExperienceClick = (id: string) => {
    setSelectedExperienceId(id);
    setExpandedExperienceId(id);
  };
  const handleCloseDetail = () => {
    setExpandedExperienceId(null);
    setSelectedExperienceId(null);
  };
  return <div className="h-full w-full flex flex-col bg-white" data-id="element-126">
      {/* Top Navbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 flex-shrink-0" data-id="element-127">
        {/* Left: Menu Button */}
        <button onClick={onMenuClick} className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors" aria-label="Menu" data-id="element-128">
          <Menu size={20} strokeWidth={1.5} data-id="element-129" />
        </button>

        {/* Center: View Toggle - Griglia/Lista */}
        <div className="flex items-center gap-1 p-1 bg-gray-50 rounded-lg" data-id="element-130">
          <button onClick={() => setViewMode('bubbles')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors" style={{
          backgroundColor: viewMode === 'bubbles' ? '#FFFFFF' : 'transparent',
          color: viewMode === 'bubbles' ? '#6C083B' : '#6B7280'
        }} data-id="element-131">
            <Grid3x3 size={14} strokeWidth={1.5} data-id="element-132" />
            Griglia
          </button>
          <button onClick={() => setViewMode('list')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors" style={{
          backgroundColor: viewMode === 'list' ? '#FFFFFF' : 'transparent',
          color: viewMode === 'list' ? '#6C083B' : '#6B7280'
        }} data-id="element-133">
            <List size={14} strokeWidth={1.5} data-id="element-134" />
            Lista
          </button>
        </div>

        {/* Right: Info Button */}
        <button className="p-2 -mr-2 text-gray-400 hover:text-gray-900 transition-colors" onClick={() => setShowInfo(!showInfo)} aria-label="Informazioni" data-id="element-135">
          <Info size={18} strokeWidth={1.5} data-id="element-136" />
        </button>
      </div>

      {/* Info tooltip */}
      {showInfo && <div className="mx-4 mt-3 p-4 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 flex-shrink-0" data-id="element-137">
          <p className="leading-relaxed" data-id="element-138">
            {viewMode === 'bubbles' ? "Ogni bolla rappresenta un'esperienza enologica. Tocca una bolla per scoprire i dettagli e i vini compatibili della tua cantina." : "Esplora le esperienze organizzate per categoria. Tocca un'esperienza per vedere i dettagli e i vini compatibili."}
          </p>
        </div>}

      {/* Main content area */}
      <div className="flex-1 relative min-h-0" data-id="element-139">
        {viewMode === 'bubbles' ? <ExperienceBubbleCluster experiences={mockExperiences} selectedExperienceId={selectedExperienceId} onSelectExperience={handleExperienceClick} data-id="element-140" /> : <ExperiencesList experiences={mockExperiences} selectedExperienceId={selectedExperienceId} onSelectExperience={handleExperienceClick} data-id="element-141" />}
      </div>

      {/* Selected experience info */}
      {selectedExperience && !expandedExperienceId && <div className="px-5 py-4 bg-white border-t border-gray-100 flex-shrink-0" data-id="element-142">
          <div className="flex items-start gap-3" data-id="element-143">
            <div className="flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0" style={{
          backgroundColor: selectedExperience.locked ? '#D7D7D7' : '#6C083B'
        }} data-id="element-144" />
            <div className="flex-1 min-w-0" data-id="element-145">
              <h2 className="text-sm text-gray-900 mb-0.5" data-id="element-146">
                {selectedExperience.name}
              </h2>
              {selectedExperience.locked ? <p className="text-xs text-gray-500" data-id="element-147">
                  Trova vini adatti per sbloccare questa esperienza
                </p> : <p className="text-xs text-gray-500" data-id="element-148">
                  {selectedExperience.bottleCount}{' '}
                  {selectedExperience.bottleCount === 1 ? 'bottiglia disponibile' : 'bottiglie disponibili'}
                </p>}
            </div>
          </div>
        </div>}

      {/* Bottom Bar - Favorites Button */}
      <div className="px-4 py-3 bg-white border-t border-gray-100 flex-shrink-0" data-id="element-149">
        <button onClick={() => setIsFavoritesOpen(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-150">
          <Star size={16} style={{
          color: '#6C083B'
        }} strokeWidth={1.5} data-id="element-151" />
          <span className="text-sm font-medium text-gray-900" data-id="element-152">
            Esperienze preferite ({favoriteExperiences.length})
          </span>
        </button>
      </div>

      {/* Legend - only show in bubble view */}
      {!expandedExperienceId && viewMode === 'bubbles' && <BubbleLegend data-id="element-153" />}

      {/* Favorites Modal */}
      <FavoritesModal isOpen={isFavoritesOpen} onClose={() => setIsFavoritesOpen(false)} favorites={favoriteExperiences} onSelectExperience={handleExperienceClick} data-id="element-154" />

      {/* Expanded detail view */}
      <AnimatePresence data-id="element-155">
        {expandedExperience && <ExperienceDetailView experience={expandedExperience} onClose={handleCloseDetail} data-id="element-156" />}
      </AnimatePresence>
    </div>;
}